import { apiService } from './api';
import { WordPartOfSpeech } from './contentService';

export const LEITNER_CHANGED_EVENT = 'app:leitnerChanged';

export function emitLeitnerChanged(detail?: { added?: boolean }) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(LEITNER_CHANGED_EVENT, { detail }));
}

export enum LeitnerSourceKind {
  Manual = 0,
  Lesson = 1,
  Book = 2
}

export interface LeitnerCardDto {
  id: number;
  persian: string;
  english: string;
  phonetic?: string;
  partOfSpeech: WordPartOfSpeech;
  example?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  boxNumber: number;
  nextReviewAt: string;
  lastReviewedAt?: string;
  reviewCount: number;
  sourceKind: LeitnerSourceKind;
  sourceLessonId?: number;
  sourceLibraryBookId?: number;
  isDue: boolean;
  createdAt: string;
}

function readLeitnerImageUrl(card: LeitnerCardDto): string | undefined {
  const raw = card as LeitnerCardDto & { ImageUrl?: string };
  const value = (raw.imageUrl ?? raw.ImageUrl)?.trim();
  return value || undefined;
}

function normalizeLeitnerCard(card: LeitnerCardDto): LeitnerCardDto {
  const imageUrl = readLeitnerImageUrl(card);
  return imageUrl ? { ...card, imageUrl } : { ...card, imageUrl: undefined };
}

function normalizeLeitnerCards(cards: LeitnerCardDto[] | undefined): LeitnerCardDto[] {
  return (cards ?? []).map(normalizeLeitnerCard);
}

export interface LeitnerCardWriteDto {
  persian: string;
  english: string;
  phonetic?: string;
  partOfSpeech?: WordPartOfSpeech;
  example?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  sourceKind?: LeitnerSourceKind;
  sourceLessonId?: number;
  sourceLibraryBookId?: number;
  sourceContentBlockId?: number;
  sourceLibraryBookBlockId?: number;
  sourceEntryKey?: string;
}

export interface LeitnerListResultDto {
  total: number;
  page: number;
  pageSize: number;
  items: LeitnerCardDto[];
}

export interface LeitnerSummaryDto {
  total: number;
  dueCount: number;
  boxCounts: number[];
}

export interface LeitnerBulkAddResultDto {
  added: number;
  alreadyPresent: number;
  cards: LeitnerCardDto[];
}

export interface LeitnerWordStatusDto {
  persian: string;
  inBox: boolean;
  cardId?: number;
}

class LeitnerService {
  async getSummary(): Promise<LeitnerSummaryDto> {
    return apiService.get<LeitnerSummaryDto>('/api/Leitner/summary', { cache: false });
  }

  async list(params: {
    q?: string;
    box?: number;
    pos?: WordPartOfSpeech;
    dueOnly?: boolean;
    page?: number;
    pageSize?: number;
  } = {}): Promise<LeitnerListResultDto> {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.box != null) query.set('box', String(params.box));
    if (params.pos != null) query.set('pos', String(params.pos));
    if (params.dueOnly) query.set('dueOnly', 'true');
    query.set('page', String(params.page ?? 1));
    query.set('pageSize', String(params.pageSize ?? 30));
    const result = await apiService.get<LeitnerListResultDto>(`/api/Leitner/cards?${query.toString()}`, { cache: false });
    return { ...result, items: normalizeLeitnerCards(result.items) };
  }

  async create(dto: LeitnerCardWriteDto): Promise<LeitnerCardDto> {
    return normalizeLeitnerCard(await apiService.post<LeitnerCardDto>('/api/Leitner/cards', dto));
  }

  async update(id: number, dto: LeitnerCardWriteDto): Promise<LeitnerCardDto> {
    return normalizeLeitnerCard(await apiService.put<LeitnerCardDto>(`/api/Leitner/cards/${id}`, dto));
  }

  async remove(id: number): Promise<void> {
    await apiService.delete(`/api/Leitner/cards/${id}`);
  }

  async bulkAdd(items: LeitnerCardWriteDto[]): Promise<LeitnerBulkAddResultDto> {
    const result = await apiService.post<LeitnerBulkAddResultDto>('/api/Leitner/cards/bulk', { items });
    return { ...result, cards: normalizeLeitnerCards(result.cards) };
  }

  async review(id: number, knew: boolean): Promise<LeitnerCardDto> {
    return normalizeLeitnerCard(await apiService.post<LeitnerCardDto>(`/api/Leitner/cards/${id}/review`, { knew }));
  }

  async status(persians: string[]): Promise<LeitnerWordStatusDto[]> {
    return apiService.post<LeitnerWordStatusDto[]>('/api/Leitner/status', { persians });
  }
}

export const leitnerService = new LeitnerService();
